<template>
  <div class="modal-content">
    <!-- Header -->
    <div class="py-0 px-0 d-flex align-center justify-space-between mb-3">
      <div>
        <span v-t="'common.backpack'" class="text-uppercase font-weight-bold" />
        <span class="ml-2 font-weight-bold">
          <span>(${{ invValue | toScrap }})</span>
        </span>
      </div>

      <div class="d-flex fw-regular flex-center">
        <v-text-field
          v-model="inputs.search"
          class="mr-2 rounded-0"
          background-color="grey800"
          hide-details solo dense flat
          :placeholder="$t('common.search')"
        />

        <!-- refresh -->
        <v-btn
          color="grey300 text--secondary" class="rounded-0 unwidth"
          :ripple="false" :disabled="loading.inventory" @click="onGetInventory"
        >
          <fai :spin="loading.inventory" :icon="['fas','sync']" />
        </v-btn>
      </div>
    </div>

    <!-- Items -->
    <div class="mb-3">
      <Inventory
        class="mb-1"
        :items="filteredItems"
        :loading="loading.inventory"
        :item-component="itemComponent"
        @onItemClick="onSelectItem"
      >
        <template #no-items>
          <div v-if="items.length && inputs.search" class="py-8 grey400 text-center">
            <span v-t="'inventory.no_items_filtered'" />
          </div>
          <BackpackEmpty
            v-else
            no-img
            class="full-height grey700 py-8"
            @deposit="onDeposit"
            @buy="onBuy"
          />
        </template>
      </Inventory>
    </div>

    <!-- Footer -->
    <div class="d-flex">
      <div class="px-5 mr-3 grey800 grey100--text d-flex justify-center flex-column">
        <div class="mb-1 d-flex align-center">
          <div
            v-if="minValue"
            class="d-flex font-weight-semibold align-center text-body-2"
            :class="selectedValue && selectedValue < minValue && 'has-error'"
          >
            <span class="text-uppercase mr-1" v-text="'Min'" />
            <span>{{ minValue | toScrap }}</span>
          </div>

          <span v-if="minValue && maxValue" class="mx-2 font-weight-bold grey200--text">
            &middot;
          </span>

          <div
            v-if="maxValue"
            class="d-flex font-weight-semibold align-center text-body-2"
            :class="selectedValue > maxValue && 'has-error'"
          >
            <span class="text-uppercase mr-1" v-text="'Max'" />
            <span>{{ maxValue | toScrap }}</span>
          </div>
        </div>
        <div class="caption faded-8 lh-1" :class="selected.length > maxItemSelect && 'has-error'">
          {{ selected.length }}<small class="faded-5 mr-1">/{{ maxItemSelect }}</small>
          <span v-t="'page.deposit.method.rust.items_selected'" />
        </div>
      </div>

      <!-- coins -->
      <div class="grey800 py-3 flex-grow-1 mr-3 px-4 d-flex align-center">
        <div class="">
          <h4 class="primary400--text text-uppercase font-weight-bold text-caption">
            Pick a side
          </h4>
          <h4
            v-if="coin[form.side]"
            v-t="coin[form.side].name"
            class="grey100--text text-body-2 lh-1-4  "
          />
        </div>

        <v-spacer />

        <v-img
          :src="coin[0].image"
          :width="48" aspect-ratio="1"
          class="flex-grow-0 mr-2 rounded-circle link"
          @click="form.side = 0"
        >
          <v-overlay absolute :value="form.side === 0">
            <fai :icon="['fas', 'check']" class="green200--text text-h6 mt-1" />
          </v-overlay>
        </v-img>
        <v-img
          :src="coin[1].image"
          :width="48" aspect-ratio="1"
          class="flex-grow-0 rounded-circle link"
          @click="form.side = 1"
        >
          <v-overlay absolute :value="form.side === 1">
            <fai :icon="['fas', 'check']" class="green200--text text-h6 mt-1" />
          </v-overlay>
        </v-img>
      </div>


      <v-btn
        color="green800 green500--text"
        class="create-btn text-none unheight" min-width="250"
        depressed :ripple="false" tile
        :disabled="!canSubmit"
        :loading="loading.create"
        @click="onSubmit"
      >
        <div class="d-flex align-center">
          <h5 class="text-uppercase text-left">
            <p class="mb-0 text-body-2 lh-1-4 font-weight-semibold">
              Create
            </p>
            <p class="mb-0 green600--text text-body-2 lh-1-4 font-weight-bold">
              Coinflip
            </p>
          </h5>
          <v-spacer />
          <div class="text-right">
            <span class="text-body-2 my-1">${{ selectedValue | toScrap }}</span>
            <div class="text-caption pos-relative z-2 green200--text font-weight-semibold lh-1">
              <span>${{ range[0] | toScrap }}</span>
              <span class="mx-1">&ndash;</span>
              <span>${{ range[1] | toScrap }}</span>
            </div>
          </div>
        </div>
      </v-btn>
    </div>
  </div>
</template>

<script>
import BackpackMixin from '@/mixins/backpack'
import BackpackEmpty from '@/components/BackpackEmpty'
import { mapActions } from 'vuex'

export default {
  components: {
    BackpackEmpty,
  },
  mixins: [BackpackMixin],
  props: {
    close: {
      type: Function,
      required: true,
    },
    payload: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: {
        create: false,
      },
      form: {
        side: 0,
      },
    }
  },
  computed: {
    // props
    maxItemSelect() {
      return this.payload?.maxItemSelect ?? 10
    },
    minValue() {
      return this.payload?.minValue ?? false
    },
    maxValue() {
      const maxValue = this.payload?.maxValue ?? false

      if (maxValue >= 9999e2) return false

      return maxValue
    },
    margin() {
      return this.payload?.margin ?? false
    },
    coin() {
      return this.payload?.coin ?? false
    },
    //
    range() {
      const { selectedValue } = this

      if (!selectedValue) return [0, 0]

      const margin = this.margin / 100

      return [
        Math.floor(selectedValue * (1 - margin)),
        Math.ceil(selectedValue * (1 + margin)),
      ]
    },
    canSubmit() {
      if (this.selected.length === 0) return false
      if (this.form.side === null) return false

      if (this.maxItemSelect && this.selected.length > this.maxItemSelect) return false
      if (this.minValue && this.selectedValue < this.minValue) return false
      if (this.maxValue && this.selectedValue > this.maxValue) return false

      return true
    },
  },
  methods: {
    ...mapActions({
      create: 'games/coinflip/create',
    }),
    async onSubmit() {
      if (this.loading.create) return

      const ids = this.selected.map(i => i.id)
      if (!ids.length) return

      this.loading.create = true

      try {
        await this.create({
          margin: this.margin,
          coin: this.coin.id,
          side: this.form.side,
          ids,
        })

        this.close()
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.create = false
      }
    },
    onDeposit() {
      this.$modal.open('backpack-deposit', { maxWidth: 900, noShadow: true })
      this.close()
    },
    onBuy() {
      this.$modal.open('backpack-intent', { maxWidth: 600, noShadow: true }, { action: 'shop' })
      this.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.inventory {
  max-height: 500px;
}

.has-error {
  color: primary(400);
  animation: bounce 1s ease 1;
}

.create-btn {
  ::v-deep .v-btn__content {
    display: block;
  }
}

@keyframes bounce {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}
</style>
